import {Button, Checkbox, Empty, Input, InputNumber, Space, Switch, Typography} from 'antd';
import {get, set} from 'lodash';
import React from 'react';
import {IItemColumn} from '../../tevapi/interfaces';


export interface IFilterControl {
  setFilterFor: (path: string, value: any) => void;
  resetFilterFor: (path: string) => void;
  setSelectedKeys: (arg0: string[]) => void;
};

export interface IColumnFilter extends IFilterControl {
  column: IItemColumn;
  filters: {[key: number]: string};
};


const inputStyle = {
  display: "block",
  width: "auto",
};

const NANSwitch: React.FC<IColumnFilter> = (props) => {
  return (
    <Switch
      checked={get(props.filters, `${props.column.pk}.allownan`, true)}
      checkedChildren="Allow NaN"
      unCheckedChildren="No NaN"
      onChange={
        (e) => {
          props.setSelectedKeys(e ? [e]: []);
          props.setFilterFor(`${props.column.pk}.allownan`, e);
        }
      }
    />
  );
};

const NullSwitch: React.FC<IColumnFilter> = (props) => {
  return (
    <Switch
      checked={get(props.filters, `${props.column.pk}.allownull`, true)}
      checkedChildren="Allow NULLs"
      unCheckedChildren="No NULLs"
      onChange={
        (e) => {
          props.setSelectedKeys(e ? [e]: []);
          props.setFilterFor(`${props.column.pk}.allownull`, e);
        }
      }
    />
  );
};


const CaseSensitiveSwitch: React.FC<IColumnFilter> = (props) => {
  return (
    <Switch
      checked={get(props.filters, `${props.column.pk}.case`, true)}
      checkedChildren="Case Sensitive"
      unCheckedChildren="case insensitive"
      onChange={
        (e) => {
          props.setSelectedKeys(e ? [e]: []);
          props.setFilterFor(`${props.column.pk}.case`, e);
        }
      }
    />
  );
};


const ExactSwitch: React.FC<IColumnFilter> = (props) => {
  return (
    <Switch
      checked={get(props.filters, `${props.column.pk}.exact`)}
      checkedChildren="Exact Search"
      unCheckedChildren="Inexact Search"
      onChange={
        (e) => {
          props.setSelectedKeys(e ? [e] : []);
          props.setFilterFor(`${props.column.pk}.exact`, e)
        }
      }
    />
  );
};


const TextSearch: React.FC<IColumnFilter> = (props) => {
    return (
      <>
        <Input
          placeholder={`Search ${props.column.name}`}
          value={get(props.filters, `${props.column.pk}.value`)}
          onChange={
            e => {
              props.setFilterFor(`${props.column.pk}.value`, e.target.value);
              props.setSelectedKeys(e.target.value ? [e.target.value] : []);
            }
          }
          style={inputStyle}
        />
        
        <ExactSwitch {...props}/>
        <NullSwitch {...props} />
        <CaseSensitiveSwitch {...props} />
      </>
    );
};


const IntegerSearch: React.FC<IColumnFilter> = (props) => {
  return (
    <>
      <InputNumber
        placeholder={`Search ${props.column.name}`}
        value={get(props.filters, `${props.column.pk}.value`)}
        onChange={
          (e) => {
            props.setFilterFor(`${props.column.pk}.value`, e);
            props.setSelectedKeys(e ? [e]: []);
          }
        }
        style={inputStyle}
      />
      <NullSwitch {...props} />
    </>
  );
};


const DecimalSearch: React.FC<IColumnFilter> = (props) => {
  return (
    <Space direction='vertical'>
      <InputNumber
        placeholder={`Search ${props.column.name}`}
        value={get(props.filters, `${props.column.pk}.value`)}
        step="0.000001"
        onChange={
          (value) => {
            props.setFilterFor(`${props.column.pk}.value`, value);
            props.setSelectedKeys(value ? [value]: []);
          }
        }
        style={inputStyle}
        stringMode
      />
      <NANSwitch {...props}/>
      <NullSwitch {...props} />
    </Space>
  );
};


const UncertainDecimalSearch: React.FC<IColumnFilter> = (props) => {
  return (
    <Space direction='vertical'>
      <InputNumber
        placeholder={`Search ${props.column.name}`}
        value={get(props.filters, `${props.column.pk}.value`)}
        step="0.000001"
        onChange={
          (e) => {
            props.setFilterFor(`${props.column.pk}.value`, e);
            props.setSelectedKeys(e ? [e]: []);
          }
        }
        style={inputStyle}
        stringMode
      />
      <Switch
        checked={get(props.filters, `${props.column.pk}.useerror`, true)}
        checkedChildren="Use Error"
        unCheckedChildren="Ignore Error"
        onChange={
          (value) => {
            props.setFilterFor(`${props.column.pk}.userror`, value);
            props.setSelectedKeys(value ? [value]: []);
          }
        }
      />
    </Space>
  );
};


const BooleanSearch: React.FC<IColumnFilter> = (props) => {
  const options = [
    {label: "True", value: "true"},
    {label: "False", value: "false"},
    {label: "Null", value: "null"}
  ];
  return (
    <>
      <Checkbox.Group
        options={options}
        defaultValue={["true", "false", "null"]}
        onChange={
          (e) => {
            props.setFilterFor(`${props.column.pk}.value`, e);
            props.setSelectedKeys(e ? [e] : []);
          }
        }
      />
    </>
  )
};

const DynamicSearch: React.FC<IColumnFilter> = (props) => {
  switch (props.column.datatype) {
    case "string":
      return <TextSearch {...props} />;
    case "decimal":
      return <DecimalSearch {...props} />;
    case "number":
      return <DecimalSearch {...props} />;
    case "integer":
      return <IntegerSearch {...props} />;
    case "boolean":
      return <BooleanSearch {...props} />;
    case "uncertain_decimal":
      return <UncertainDecimalSearch {...props} />;
  }
  return (
    <Typography.Paragraph type='secondary'>
      Datatype {props.column.datatype} cannot be filtered at the present.
    </Typography.Paragraph>
  );
}


export const ColumnControl: React.FC<IColumnFilter> = (props) => {
  return (
    <div style={{padding: 8}} onKeyDown={(e) => e.stopPropagation()}>
      <Space direction="vertical">
        <DynamicSearch {...props} />
        <Space direction='horizontal'>
          <Button
            type="primary"
            size="small"
            //onClick={() => {props.confirm({closeDropdown: false})}}
          >
            Search
          </Button>
          <Button
            size="small"
            onClick={
              () => props.resetFilterFor(`${props.column.pk}`)
            }>
            Reset
          </Button>
        </Space>
      </Space>
    </div>
  );
};
